import { useMutation, useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS } from '@/constants';
import {
  handleError,
  handleResponse,
  get,
  post,
  put,
  del,
  getFile
} from './base.service';
import { handleError as appHandleError } from '@/utils/errorHandler';
import { downloadReportFile } from '@/utils/common';

interface FileExportResult {
  success: boolean;
  filename?: string;
}

export const useCreateNewStaffMutation = () =>
  useMutation({
    mutationFn: (payload: Record<string, unknown>) => {
      return post(`${API_ENDPOINTS.STAFF_MANAGERS}`, payload);
    },
    onSuccess: handleResponse,
    onError: handleError
  });

export const useEditStaffMutation = () =>
  useMutation({
    mutationFn: (payload: Record<string, unknown>) => {
      return put(`${API_ENDPOINTS.STAFF_MANAGERS}/${payload.id}`, payload);
    },
    onSuccess: handleResponse,
    onError: handleError
  });

export const useGetStaffDetailsQuery = (id: number) =>
  useQuery({
    queryKey: ['staff-details', id],
    queryFn: async () => {
      const response = await get(`${API_ENDPOINTS.STAFF_MANAGERS}/${id}`);
      // Extract data from nested response structure
      return response?.data || response;
    },
    enabled: !!id,
    refetchOnWindowFocus: false
  });

export const useGetStaffListQuery = (queryString: string) =>
  useQuery({
    queryKey: ['staff-list', queryString],
    queryFn: () => get(`${API_ENDPOINTS.STAFF_MANAGERS}?${queryString}`),
    refetchOnWindowFocus: false
  });

export const useDeleteStaffMutation = () =>
  useMutation({
    mutationFn: (id: number) => {
      return del(`${API_ENDPOINTS.STAFF_MANAGERS}/${id}`);
    },
    onSuccess: handleResponse,
    onError: handleError
  });

export const useStaffReportQuery = (payload: string) =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(
          `${API_ENDPOINTS.STAFF_MANAGER_REPORT}?${payload}`
        );
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        return { success: false, data: null };
      }
    },
    queryKey: ['staff-report', payload],
    refetchOnWindowFocus: false
  });

export const useStaffReportByIdQuery = (staffId: string, queryString: string) =>
  useQuery({
    queryKey: ['staff-report-detail', staffId, queryString],
    queryFn: async () => {
      const response = await get(
        `${API_ENDPOINTS.STAFF_MANAGER_REPORT}?${queryString}`
      );
      return response?.data || response;
    },
    refetchOnWindowFocus: false,
    enabled: !!staffId && !!queryString
  });

export const useStaffReportExportQuery = (
  payload: string,
  enabled: boolean = true
) =>
  useQuery<FileExportResult>({
    queryKey: ['staff-report-export', payload],
    enabled: Boolean(payload) && enabled,
    refetchOnWindowFocus: false,
    retry: false,

    queryFn: async () => {
      const url = `${API_ENDPOINTS.STAFF_MANAGER_REPORT_EXPORT}?${payload}`;
      const { blob, filename } = await getFile(url);

      // Reusable download handler
      downloadReportFile(blob, payload, 'staff-report');

      return { success: true, filename };
    }
  });

export const useStaffReportExportByIdQuery = (
  staffId: string | number,
  queryString: string,
  enabled: boolean = true
) =>
  useQuery<FileExportResult>({
    queryKey: ['staff-report-export-by-id', staffId, queryString],
    enabled: Boolean(staffId) && Boolean(queryString) && enabled,
    refetchOnWindowFocus: false,
    retry: false,

    queryFn: async () => {
      // Build the URL with staff_id as a query parameter
      const url = `${API_ENDPOINTS.STAFF_MANAGER_REPORT_EXPORT}?${queryString}`;
      const { blob, filename } = await getFile(url);

      // Reusable download handler
      downloadReportFile(blob, queryString, `staff-report-${staffId}`);

      return { success: true, filename };
    }
  });
